import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import {
    Box,
    Container,
    Grid,
    Paper,
    Typography,
    Button,
    TextField,
    InputAdornment,
    IconButton,
    Breadcrumbs,
    Link,
    Chip,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    MenuItem,
    Select,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    Search as SearchIcon,
    Science as ScienceIcon,
    Biotech as BiotechIcon,
    Assessment as AssessmentIcon,
    CheckCircle as CheckCircleIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import toastr from 'toastr';
import * as QcViewerService from '../../../services/clinicPortalServices/qcViewerService';
import RunDetail from './RunDetail';
import SampleDetail from './SampleDetail';
import { formatNumber } from '../../../services/common/util';

const QcViewer = () => {
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(0);  // 0=Runs, 1=Samples, 2=Reports

    // Stats
    const [stats, setStats] = useState({ runCount: 0, sampleCount: 0, reportCount: 0, testResultCount: 0 });

    // QC Runs state
    const [runs, setRuns] = useState([]);
    const [runSearch, setRunSearch] = useState('');
    const [runPendingSearch, setRunPendingSearch] = useState('');
    const [runPagination, setRunPagination] = useState({ page: 1, limit: 25, totalCount: 0, totalPages: 0 });
    const [runSortField, setRunSortField] = useState('runDate');
    const [runSortOrder, setRunSortOrder] = useState('desc');
    const [selectedRun, setSelectedRun] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    // Filters
    const [filterOptions, setFilterOptions] = useState({ instruments: [], analytes: [] });
    const [instrumentFilter, setInstrumentFilter] = useState('');
    const [analyteFilter, setAnalyteFilter] = useState('');
    const [dateFromFilter, setDateFromFilter] = useState('');
    const [dateToFilter, setDateToFilter] = useState('');

    // QC Samples state
    const [samples, setSamples] = useState([]);
    const [sampleSearch, setSampleSearch] = useState('');
    const [samplePendingSearch, setSamplePendingSearch] = useState('');
    const [samplePagination, setSamplePagination] = useState({ page: 1, limit: 25, totalCount: 0, totalPages: 0 });
    const [selectedSample, setSelectedSample] = useState(null);

    // QC Reports state
    const [reports, setReports] = useState([]);
    const [reportSearch, setReportSearch] = useState('');
    const [reportPendingSearch, setReportPendingSearch] = useState('');
    const [reportPagination, setReportPagination] = useState({ page: 1, limit: 25, totalCount: 0, totalPages: 0 });

    const runGridRef = useRef(null);
    const sampleGridRef = useRef(null);
    const reportGridRef = useRef(null);

    // Load on mount
    useEffect(() => {
        loadStats();
        loadFilterOptions();
        loadRuns('', 1, 25, 'runDate', 'desc', {});
    }, []);

    const loadStats = async () => {
        try {
            const response = await QcViewerService.getQcStats();
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('Failed to load QC stats:', error);
        }
    };

    const loadFilterOptions = async () => {
        try {
            const response = await QcViewerService.getQcFilterOptions();
            if (response.success) {
                setFilterOptions(response.data);
            }
        } catch (error) {
            console.error('Failed to load filter options:', error);
        }
    };

    // ============ QC RUNS ============
    const loadRuns = async (query, page, limit, sortFld, sortOrd, filters) => {
        try {
            setLoading(true);
            const response = await QcViewerService.searchQcRuns(query, page, limit, sortFld, sortOrd, filters);
            if (response.success) {
                setRuns(response.data);
                setRunPagination(response.pagination);
            }
        } catch (error) {
            toastr.error('Failed to load QC runs');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const currentFilters = () => ({
        instrument: instrumentFilter,
        analyte: analyteFilter,
        dateFrom: dateFromFilter,
        dateTo: dateToFilter
    });

    const handleRunSearch = useCallback(() => {
        setRunSearch(runPendingSearch);
        loadRuns(runPendingSearch, 1, runPagination.limit, runSortField, runSortOrder, currentFilters());
    }, [runPendingSearch, runPagination.limit, runSortField, runSortOrder, instrumentFilter, analyteFilter, dateFromFilter, dateToFilter]);

    const handleRunSearchKeyDown = (e) => {
        if (e.key === 'Enter') handleRunSearch();
    };

    const handleClearRunSearch = () => {
        setRunPendingSearch('');
        setRunSearch('');
        loadRuns('', 1, runPagination.limit, runSortField, runSortOrder, currentFilters());
    };

    const handleRunPageChange = (newPage) => {
        loadRuns(runSearch, newPage, runPagination.limit, runSortField, runSortOrder, currentFilters());
    };

    const handleRunSortChanged = (event) => {
        const columnState = event.api.getColumnState();
        const sortedCol = columnState.find(c => c.sort);
        if (sortedCol) {
            const newSortField = sortedCol.colId;
            const newSortOrder = sortedCol.sort;
            setRunSortField(newSortField);
            setRunSortOrder(newSortOrder);
            loadRuns(runSearch, 1, runPagination.limit, newSortField, newSortOrder, currentFilters());
        }
    };

    const handleRunRowClicked = async (event) => {
        try {
            setDetailLoading(true);
            const response = await QcViewerService.getQcRun(event.data._id);
            if (response.success) {
                setSelectedRun(response.data);
            }
        } catch (error) {
            toastr.error('Failed to load QC run details');
            console.error(error);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleFilterChange = (filterType, value) => {
        const newFilters = { ...currentFilters(), [filterType]: value };
        if (filterType === 'instrument') setInstrumentFilter(value);
        if (filterType === 'analyte') setAnalyteFilter(value);
        if (filterType === 'dateFrom') setDateFromFilter(value);
        if (filterType === 'dateTo') setDateToFilter(value);
        loadRuns(runSearch, 1, runPagination.limit, runSortField, runSortOrder, newFilters);
    };

    // ============ QC SAMPLES ============
    const loadSamples = async (query, page, limit) => {
        try {
            setLoading(true);
            const response = await QcViewerService.searchQcSamples(query, page, limit);
            if (response.success) {
                setSamples(response.data);
                setSamplePagination(response.pagination);
            }
        } catch (error) {
            toastr.error('Failed to load QC samples');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSampleSearch = useCallback(() => {
        setSampleSearch(samplePendingSearch);
        loadSamples(samplePendingSearch, 1, samplePagination.limit);
    }, [samplePendingSearch, samplePagination.limit]);

    const handleSampleSearchKeyDown = (e) => {
        if (e.key === 'Enter') handleSampleSearch();
    };

    const handleClearSampleSearch = () => {
        setSamplePendingSearch('');
        setSampleSearch('');
        loadSamples('', 1, samplePagination.limit);
    };

    const handleSampleRowClicked = async (event) => {
        try {
            setDetailLoading(true);
            const response = await QcViewerService.getQcSample(event.data._id);
            if (response.success) {
                setSelectedSample(response.data);
            }
        } catch (error) {
            toastr.error('Failed to load QC sample details');
            console.error(error);
        } finally {
            setDetailLoading(false);
        }
    };

    // ============ QC REPORTS ============
    const loadReports = async (query, page, limit) => {
        try {
            setLoading(true);
            const response = await QcViewerService.searchQcReports(query, page, limit);
            if (response.success) {
                setReports(response.data);
                setReportPagination(response.pagination);
            }
        } catch (error) {
            toastr.error('Failed to load QC reports');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleReportSearch = useCallback(() => {
        setReportSearch(reportPendingSearch);
        loadReports(reportPendingSearch, 1, reportPagination.limit);
    }, [reportPendingSearch, reportPagination.limit]);

    const handleReportSearchKeyDown = (e) => {
        if (e.key === 'Enter') handleReportSearch();
    };

    const handleClearReportSearch = () => {
        setReportPendingSearch('');
        setReportSearch('');
        loadReports('', 1, reportPagination.limit);
    };

    // ============ TAB CHANGE ============
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        if (newValue === 1 && samples.length === 0) {
            loadSamples('', 1, 25);
        }
        if (newValue === 2 && reports.length === 0) {
            loadReports('', 1, 25);
        }
    };

    // ============ COLUMN DEFS ============
    const formatDate = (params) => {
        if (!params.value) return '';
        const d = new Date(params.value);
        return isNaN(d.getTime()) ? params.value : d.toLocaleDateString();
    };

    const runColumnDefs = [
        { headerName: 'Run Name', field: 'runName', sortable: true, width: 130 },
        { headerName: 'Run Date', field: 'runDate', sortable: true, width: 110, valueFormatter: formatDate },
        { headerName: 'Instrument', field: 'instrument', sortable: true, width: 160 },
        { headerName: '# Results', field: 'resultCount', sortable: true, width: 100, type: 'numericColumn' },
        { headerName: '# Analytes', field: 'analyteCount', sortable: false, width: 100, type: 'numericColumn' },
        { headerName: 'Analytes', field: 'analytes', sortable: false, flex: 2, minWidth: 200, tooltipField: 'analytes' },
        { headerName: 'QC Samples', field: 'qcSamples', sortable: false, flex: 1, minWidth: 150, tooltipField: 'qcSamples' }
    ];

    const sampleColumnDefs = [
        { headerName: 'Sample Name', field: 'sampleName', sortable: true, flex: 1, minWidth: 150 },
        { headerName: 'Control Type', field: 'controlType', sortable: true, width: 120 },
        { headerName: 'Lot Number', field: 'lotNumber', sortable: true, width: 140 },
        { headerName: 'Lot Expiration', field: 'lotExpiration', sortable: true, width: 130, valueFormatter: formatDate },
        { headerName: '# Analytes', field: 'analyteCount', sortable: false, width: 100, type: 'numericColumn' },
        { headerName: 'Analytes', field: 'analytes', sortable: false, flex: 2, minWidth: 250, tooltipField: 'analytes' }
    ];

    const reportColumnDefs = [
        {
            headerName: 'Report',
            field: 'ljReportPath',
            sortable: false,
            width: 100,
            cellRenderer: memo((params) => {
                if (!params.value) return '';
                const url = QcViewerService.getLjReportUrl(params.value);
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer"
                       style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500 }}
                       onClick={(e) => e.stopPropagation()}>
                        View
                    </a>
                );
            })
        },
        { headerName: 'Report Name', field: 'reportName', sortable: true, flex: 2, minWidth: 300, tooltipField: 'reportName' },
        { headerName: 'Start Date', field: 'startDate', sortable: true, width: 110, valueFormatter: formatDate },
        { headerName: 'End Date', field: 'endDate', sortable: true, width: 110, valueFormatter: formatDate },
        { headerName: 'Approved By', field: 'approvedBy', sortable: true, width: 140 },
        { headerName: 'Approved On', field: 'approvedOn', sortable: true, width: 110, valueFormatter: formatDate },
        { headerName: 'Instruments', field: 'instruments', sortable: false, width: 160, tooltipField: 'instruments' },
        { headerName: 'Analytes', field: 'analytes', sortable: false, flex: 1, minWidth: 150, tooltipField: 'analytes' }
    ];

    const defaultColDef = {
        resizable: true,
        filter: false
    };

    // ============ RENDER HELPERS ============
    const renderPagination = (pagination, onPageChange) => {
        if (pagination.totalPages <= 1) return null;
        return (
            <Box display="flex" justifyContent="center" alignItems="center" mt={2} gap={2}>
                <Button
                    size="small"
                    disabled={pagination.page <= 1}
                    onClick={() => onPageChange(pagination.page - 1)}
                >
                    Previous
                </Button>
                <Typography variant="body2">
                    Page {pagination.page} of {pagination.totalPages}
                </Typography>
                <Button
                    size="small"
                    disabled={pagination.page >= pagination.totalPages}
                    onClick={() => onPageChange(pagination.page + 1)}
                >
                    Next
                </Button>
            </Box>
        );
    };

    const renderSearchBar = (pendingValue, onChange, onKeyDown, onSearch, onClear, activeSearch, totalCount, placeholder) => (
        <Paper sx={{ p: 2, mb: 3 }}>
            <Box display="flex" gap={2} alignItems="center">
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder={placeholder}
                    value={pendingValue}
                    onChange={(e) => onChange(e.target.value)}
                    onKeyDown={onKeyDown}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                        endAdornment: pendingValue && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={onClear}>
                                    <ClearIcon />
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                />
                <Button variant="contained" onClick={onSearch} disabled={loading}>
                    Search
                </Button>
            </Box>
            {activeSearch && (
                <Box mt={1}>
                    <Chip label={`Search: "${activeSearch}"`} onDelete={onClear} size="small" />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        {formatNumber(totalCount)} result{totalCount !== 1 ? 's' : ''}
                    </Typography>
                </Box>
            )}
        </Paper>
    );

    const renderGrid = (gridRef, rowData, columnDefs, onRowClicked, onSortChanged, emptyMessage) => (
        <Paper sx={{ width: '100%', height: 'calc(100vh - 520px)', minHeight: 350 }}>
            {loading && rowData.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                    <CircularProgress />
                </Box>
            ) : rowData.length === 0 && !loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={4}>
                    <Alert severity="info">{emptyMessage}</Alert>
                </Box>
            ) : (
                <div className="ag-theme-balham" style={{ width: '100%', height: '100%' }}>
                    <AgGridReact
                        ref={gridRef}
                        rowData={rowData}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        onRowClicked={onRowClicked}
                        onSortChanged={onSortChanged}
                        rowSelection="single"
                        suppressCellFocus={true}
                        enableBrowserTooltips={true}
                        getRowId={(params) => params.data._id}
                        rowStyle={{ cursor: 'pointer' }}
                    />
                </div>
            )}
        </Paper>
    );

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Header */}
            <Box mb={3}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link color="inherit" href="/clinic/dashboard">
                        Clinic Portal
                    </Link>
                    <Typography color="text.primary">Legacy QC Records</Typography>
                </Breadcrumbs>

                <Typography variant="h4" component="h1" sx={{ mt: 2 }}>
                    Legacy QC Records
                </Typography>
            </Box>

            {/* Stats Badges */}
            <Grid container spacing={3} mb={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                        <ScienceIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                        <Box>
                            <Typography color="text.secondary" variant="body2">QC Runs</Typography>
                            <Typography variant="h5">{formatNumber(stats.runCount)}</Typography>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                        <CheckCircleIcon sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
                        <Box>
                            <Typography color="text.secondary" variant="body2">Test Results</Typography>
                            <Typography variant="h5">{formatNumber(stats.testResultCount)}</Typography>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                        <BiotechIcon sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
                        <Box>
                            <Typography color="text.secondary" variant="body2">QC Samples</Typography>
                            <Typography variant="h5">{formatNumber(stats.sampleCount)}</Typography>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                        <AssessmentIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                        <Box>
                            <Typography color="text.secondary" variant="body2">L-J Reports</Typography>
                            <Typography variant="h5">{formatNumber(stats.reportCount)}</Typography>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab label={`QC Runs (${formatNumber(stats.runCount)})`} />
                    <Tab label={`QC Samples (${formatNumber(stats.sampleCount)})`} />
                    <Tab label={`L-J Reports (${formatNumber(stats.reportCount)})`} />
                </Tabs>
            </Paper>

            {/* ============ QC RUNS TAB ============ */}
            {activeTab === 0 && (
                <>
                    {renderSearchBar(
                        runPendingSearch,
                        setRunPendingSearch,
                        handleRunSearchKeyDown,
                        handleRunSearch,
                        handleClearRunSearch,
                        runSearch,
                        runPagination.totalCount,
                        'Search by run name, instrument, analyte, or sample...'
                    )}

                    {/* Filters */}
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Instrument</InputLabel>
                                    <Select
                                        value={instrumentFilter}
                                        onChange={(e) => handleFilterChange('instrument', e.target.value)}
                                        label="Instrument"
                                    >
                                        <MenuItem value="">All Instruments</MenuItem>
                                        {filterOptions.instruments.map(inst => (
                                            <MenuItem key={inst} value={inst}>{inst}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Analyte</InputLabel>
                                    <Select
                                        value={analyteFilter}
                                        onChange={(e) => handleFilterChange('analyte', e.target.value)}
                                        label="Analyte"
                                    >
                                        <MenuItem value="">All Analytes</MenuItem>
                                        {filterOptions.analytes.map(a => (
                                            <MenuItem key={a} value={a}>{a}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="From Date"
                                    type="date"
                                    value={dateFromFilter}
                                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="To Date"
                                    type="date"
                                    value={dateToFilter}
                                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                        setInstrumentFilter('');
                                        setAnalyteFilter('');
                                        setDateFromFilter('');
                                        setDateToFilter('');
                                        loadRuns(runSearch, 1, runPagination.limit, runSortField, runSortOrder, {});
                                    }}
                                >
                                    Clear Filters
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {renderGrid(
                        runGridRef,
                        runs,
                        runColumnDefs,
                        handleRunRowClicked,
                        handleRunSortChanged,
                        runSearch ? 'No QC runs match your search.' : 'No legacy QC runs found.'
                    )}
                    {renderPagination(runPagination, handleRunPageChange)}
                </>
            )}

            {/* ============ QC SAMPLES TAB ============ */}
            {activeTab === 1 && (
                <>
                    {renderSearchBar(
                        samplePendingSearch,
                        setSamplePendingSearch,
                        handleSampleSearchKeyDown,
                        handleSampleSearch,
                        handleClearSampleSearch,
                        sampleSearch,
                        samplePagination.totalCount,
                        'Search by sample name, control type, lot number, or analyte...'
                    )}
                    {renderGrid(
                        sampleGridRef,
                        samples,
                        sampleColumnDefs,
                        handleSampleRowClicked,
                        null,
                        sampleSearch ? 'No QC samples match your search.' : 'No legacy QC samples found.'
                    )}
                    {renderPagination(samplePagination, (p) => loadSamples(sampleSearch, p, samplePagination.limit))}
                </>
            )}

            {/* ============ QC REPORTS TAB ============ */}
            {activeTab === 2 && (
                <>
                    {renderSearchBar(
                        reportPendingSearch,
                        setReportPendingSearch,
                        handleReportSearchKeyDown,
                        handleReportSearch,
                        handleClearReportSearch,
                        reportSearch,
                        reportPagination.totalCount,
                        'Search by report name, analyte, or instrument...'
                    )}
                    {renderGrid(
                        reportGridRef,
                        reports,
                        reportColumnDefs,
                        null,
                        null,
                        reportSearch ? 'No QC reports match your search.' : 'No legacy QC reports found.'
                    )}
                    {renderPagination(reportPagination, (p) => loadReports(reportSearch, p, reportPagination.limit))}
                </>
            )}

            {/* Run Detail Dialog */}
            {selectedRun && (
                <RunDetail run={selectedRun} onClose={() => setSelectedRun(null)} />
            )}

            {/* Sample Detail Dialog */}
            {selectedSample && (
                <SampleDetail sample={selectedSample} onClose={() => setSelectedSample(null)} />
            )}

            {/* Loading overlay */}
            {detailLoading && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        zIndex: 9999
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
        </Container>
    );
};

export default QcViewer;
